import { box, type Box } from '../geometry/box';
import type { Track } from '../tracking/track';
import { cellAt, type ScreenActivity } from './activity';

/**
 * Notification guard: banners that slide in at a corner of the screen (a new
 * message, a mail preview) can show names and text nobody meant to share. They
 * look alike everywhere: a compact rectangle appears near the top-right
 * (macOS, most Linux desktops) or bottom-right (Windows) corner while the rest
 * of the screen stays still, sits for a few seconds, then leaves. Each one
 * found becomes a hidden area, privacy first; the person can reveal it.
 */

export interface NotificationHit {
  readonly id: string;
  readonly start: number;
  readonly end: number;
  /** Normalized, with a little margin. */
  readonly box: Box;
}

/**
 * A cell counts as changed when this share of it changed. Light banners over
 * light windows barely differ in their fill; their icon and text do the talking.
 */
const CELL_CHANGED = 0.12;
/**
 * Banner size limits, as shares of the screen. Real banners are a fifth to a
 * quarter of the width (macOS ~24 %, Windows ~19 %); a menu-bar clock or a tray
 * icon ticking over is far narrower and must not count.
 */
const SIZE = { minW: 0.15, maxW: 0.42, minH: 0.04, maxH: 0.3 } as const;
/** How much of the screen outside the banner may change at the same moment. */
const QUIET_ELSEWHERE = 0.04;
/** Banners stay for a few seconds; without a visible exit, assume this long. */
const DEFAULT_STAY_SEC = 6;
const MAX_STAY_SEC = 15;
const MIN_STAY_SEC = 1;
const MARGIN = 0.012;

interface CellBox {
  readonly c0: number;
  readonly c1: number;
  readonly r0: number;
  readonly r1: number;
}

/** The corner zones (in cells) where notifications appear. */
function zones(cols: number, rows: number): CellBox[] {
  const fromCol = Math.floor(cols * 0.55);
  return [
    { c0: fromCol, c1: cols - 1, r0: 0, r1: Math.ceil(rows * 0.45) - 1 },
    { c0: fromCol, c1: cols - 1, r0: Math.floor(rows * 0.55), r1: rows - 1 },
  ];
}

/** Bounding box of changed cells inside a zone, if they form a dense rectangle. */
function bannerIn(activity: ScreenActivity, index: number, zone: CellBox): CellBox | null {
  let c0 = Infinity;
  let c1 = -1;
  let r0 = Infinity;
  let r1 = -1;
  let count = 0;
  for (let r = zone.r0; r <= zone.r1; r++) {
    for (let c = zone.c0; c <= zone.c1; c++) {
      if (cellAt(activity, index, c, r) < CELL_CHANGED) continue;
      count++;
      c0 = Math.min(c0, c);
      c1 = Math.max(c1, c);
      r0 = Math.min(r0, r);
      r1 = Math.max(r1, r);
    }
  }
  if (count === 0) return null;
  const { cols, rows } = activity.grid;
  const w = (c1 - c0 + 1) / cols;
  const h = (r1 - r0 + 1) / rows;
  const dense = count / ((c1 - c0 + 1) * (r1 - r0 + 1)) >= 0.4;
  // Banners hug the right edge of the screen; content inside the shared app rarely does.
  const atEdge = c1 >= cols - 2;
  if (!dense || !atEdge || w < SIZE.minW || w > SIZE.maxW || h < SIZE.minH || h > SIZE.maxH) return null;
  return { c0, c1, r0, r1 };
}

/** Share of cells outside `area` that changed. */
function changeOutside(activity: ScreenActivity, index: number, area: CellBox): number {
  const { cols, rows } = activity.grid;
  let changed = 0;
  let total = 0;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (c >= area.c0 - 1 && c <= area.c1 + 1 && r >= area.r0 - 1 && r <= area.r1 + 1) continue;
      total++;
      if (cellAt(activity, index, c, r) >= CELL_CHANGED) changed++;
    }
  }
  return total > 0 ? changed / total : 0;
}

/** Share of `area`'s cells that changed. */
function changeInside(activity: ScreenActivity, index: number, area: CellBox): number {
  let changed = 0;
  for (let r = area.r0; r <= area.r1; r++) for (let c = area.c0; c <= area.c1; c++) if (cellAt(activity, index, c, r) >= CELL_CHANGED) changed++;
  return changed / ((area.c1 - area.c0 + 1) * (area.r1 - area.r0 + 1));
}

const grow = (a: CellBox, b: CellBox): CellBox => ({ c0: Math.min(a.c0, b.c0), c1: Math.max(a.c1, b.c1), r0: Math.min(a.r0, b.r0), r1: Math.max(a.r1, b.r1) });

export function findNotifications(activity: ScreenActivity): NotificationHit[] {
  const { samples, grid, interval } = activity;
  const hits: NotificationHit[] = [];
  let index = 1;
  while (index < samples.length) {
    let found: CellBox | null = null;
    for (const zone of zones(grid.cols, grid.rows)) {
      const banner = bannerIn(activity, index, zone);
      if (banner && changeOutside(activity, index, banner) <= QUIET_ELSEWHERE) {
        found = banner;
        break;
      }
    }
    if (!found) {
      index++;
      continue;
    }
    // Sliding in can take a sample or two; the banner is the union.
    let area: CellBox = found;
    let settled = index + 1;
    while (settled < samples.length && samples[settled]!.t - samples[index]!.t < 1) {
      const more = zones(grid.cols, grid.rows)
        .map((zone) => bannerIn(activity, settled, zone))
        .find((banner): banner is CellBox => banner !== null && !(banner.c1 < area.c0 - 1 || banner.c0 > area.c1 + 1 || banner.r1 < area.r0 - 1 || banner.r0 > area.r1 + 1));
      if (!more) break;
      area = grow(area, more);
      settled++;
    }
    const start = samples[index]!.t - interval;
    // It leaves when its area changes again; the screen around it may do anything meanwhile.
    let exit = -1;
    for (let next = settled + 1; next < samples.length && samples[next]!.t - start <= MAX_STAY_SEC; next++) {
      if (samples[next]!.t - start >= MIN_STAY_SEC && changeInside(activity, next, area) >= 0.5) {
        exit = next;
        break;
      }
    }
    const end = exit >= 0 ? samples[exit]!.t + interval : Math.min(samples.at(-1)!.t, start + DEFAULT_STAY_SEC);
    const { cols, rows } = grid;
    const x = Math.max(0, area.c0 / cols - MARGIN);
    const y = Math.max(0, area.r0 / rows - MARGIN);
    hits.push({
      id: `notification-${Math.round(start * 10)}`,
      start,
      end,
      box: box(x, y, Math.min(1, (area.c1 + 1) / cols + MARGIN) - x, Math.min(1, (area.r1 + 1) / rows + MARGIN) - y),
    });
    // Skip past it, including the exit's own change.
    let resume = exit >= 0 ? exit + 1 : settled;
    while (resume < samples.length && samples[resume]!.t <= end) resume++;
    index = Math.max(resume, index + 1);
  }
  return hits;
}

/** Hidden areas for the notifications found: a still box from when one appears until it leaves. */
export const notificationTracks = (hits: readonly NotificationHit[]): Track[] =>
  hits.map((hit) => ({
    id: hit.id,
    kind: 'manual',
    samples: [
      { t: hit.start, box: hit.box, score: 1 },
      { t: hit.end, box: hit.box, score: 1 },
    ],
  }));

export const isNotificationTrack = (track: Pick<Track, 'id'>): boolean => track.id.startsWith('notification-');
